import { useEffect, useState } from 'react';
import { Link, Navigate, NavLink, Outlet, useParams } from 'react-router-dom';
import { CONSOLE_BUILD_ID, WITNESS_ENV } from '../../lib/console-env';
import {
  ApiNotConfiguredError,
  getTenant,
  type Tenant,
} from '../../lib/witness-api';
import { configureTenantAuth, hasActiveTenantSession } from '../../lib/tenant-auth';

interface TenantSection {
  readonly to: string;
  readonly label: string;
}

/**
 * Tenant-shell nav entries. Order and set come from
 * `apps/console/README.md` → Destination shape → Tenant shell.
 * Overview is the bare `/t/:tenantId/` landing (index route).
 */
const TENANT_SECTIONS: readonly TenantSection[] = [
  { to: '', label: 'Overview' },
  { to: 'ceremonies', label: 'Ceremonies' },
  { to: 'metrics', label: 'Metrics' },
  { to: 'users', label: 'Users' },
  { to: 'locations', label: 'Locations' },
  { to: 'integrations', label: 'Integrations' },
  { to: 'templates', label: 'Templates' },
  { to: 'billing', label: 'Billing' },
  { to: 'audit', label: 'Audit' },
];

type LoadState =
  | { status: 'loading' }
  | { status: 'not_found' }
  | { status: 'error'; message: string }
  | { status: 'ready'; tenant: Tenant };

export interface TenantOutletContext {
  readonly tenant: Tenant;
}

export function TenantLayout({ condensed = false }: { condensed?: boolean }) {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    if (!tenantId) {
      setState({ status: 'not_found' });
      return;
    }
    const controller = new AbortController();
    setState({ status: 'loading' });
    configureTenantAuth()
      .then(hasActiveTenantSession)
      .then((active) => {
        if (!active) throw new Error('sign_in_required');
        return getTenant(tenantId, { signal: controller.signal });
      })
      .then((tenant) => {
        if (tenant === null) setState({ status: 'not_found' });
        else setState({ status: 'ready', tenant });
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        const message =
          err instanceof Error && err.message === 'sign_in_required'
            ? 'sign_in_required'
            :
          err instanceof ApiNotConfiguredError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'failed to load tenant';
        setState({ status: 'error', message });
      });
    return () => controller.abort();
  }, [tenantId]);

  const envBadgeClass =
    WITNESS_ENV === 'prod'
      ? 'bg-neutral-900 text-neutral-50'
      : 'bg-amber-500 text-amber-950';

  const tenant = state.status === 'ready' ? state.tenant : null;

  if (state.status === 'error' && state.message === 'sign_in_required') {
    return <Navigate to={`/tenant/login?destination=${encodeURIComponent(window.location.pathname + window.location.search)}`} replace />;
  }

  if (condensed && tenant) {
    return <Outlet context={{ tenant } satisfies TenantOutletContext} />;
  }

  return (
    <div className="min-h-screen">
      <header className="border-b border-neutral-200 bg-neutral-50">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-3">
          <span
            className={`rounded px-2 py-0.5 text-xs font-bold uppercase tracking-wider ${envBadgeClass}`}
          >
            {WITNESS_ENV}
          </span>
          <Link
            to="/"
            className="text-sm text-neutral-600 hover:text-neutral-900"
          >
            Witness console
          </Link>
        </div>
      </header>

      {tenant && (
        <>
          <div className="border-b border-neutral-200 bg-white">
            <div className="mx-auto max-w-6xl px-6 py-6">
              <h1 className="font-serif text-3xl tracking-tight text-neutral-900">
                {tenant.displayName}
              </h1>
              <p className="mt-1 font-mono text-xs text-neutral-500">
                {tenant.id}
              </p>
            </div>
          </div>
          <nav className="border-b border-neutral-200 bg-white">
            <div className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-6">
              {TENANT_SECTIONS.map((section) => (
                <NavLink
                  key={section.to || 'overview'}
                  to={section.to}
                  end={section.to === ''}
                  className={({ isActive }) =>
                    `whitespace-nowrap border-b-2 px-3 py-3 text-sm transition-colors ${
                      isActive
                        ? 'border-neutral-900 font-medium text-neutral-900'
                        : 'border-transparent text-neutral-600 hover:text-neutral-900'
                    }`
                  }
                >
                  {section.label}
                </NavLink>
              ))}
            </div>
          </nav>
        </>
      )}

      <main className="mx-auto max-w-6xl px-6 py-8">
        {state.status === 'loading' && (
          <p className="text-sm text-neutral-500">Loading tenant…</p>
        )}
        {state.status === 'not_found' && (
          <div className="flex flex-col gap-3">
            <h1 className="font-serif text-2xl tracking-tight text-neutral-900">
              Tenant not found
            </h1>
            <p className="text-sm text-neutral-600">
              No tenant with id{' '}
              <span className="font-mono">{tenantId ?? '(missing)'}</span>.
            </p>
            <p className="text-sm">
              <Link
                to="/platform/tenants"
                className="text-neutral-900 underline hover:no-underline"
              >
                Back to tenants
              </Link>
            </p>
          </div>
        )}
        {state.status === 'error' && (
          <div className="rounded border border-red-300 bg-red-50 px-4 py-3 text-sm">
            <p className="text-red-900">
              Failed to load tenant: {state.message}
            </p>
            <Link className="mt-2 inline-block underline" to={`/tenant/login?destination=${encodeURIComponent(window.location.pathname + window.location.search)}`}>
              Sign in again or use a different account
            </Link>
          </div>
        )}
        {tenant && (
          <Outlet context={{ tenant } satisfies TenantOutletContext} />
        )}
      </main>

      <footer className="mx-auto max-w-6xl px-6 py-6 font-mono text-xs text-neutral-500">
        build {CONSOLE_BUILD_ID}
      </footer>
    </div>
  );
}
